const SCROLL_EPS = 2;

class ProductGalleryCarouselElement extends HTMLElement {
  #abortController;
  #resizeObserver;

  /**
   * @param {HTMLElement} viewport
   * @returns {HTMLElement[]}
   */
  #getSlides(viewport) {
    return [...viewport.querySelectorAll(".gallery__slide")];
  }

  /**
   * @param {HTMLElement} viewport
   */
  #maxScroll(viewport) {
    return Math.max(0, viewport.scrollWidth - viewport.clientWidth);
  }

  /**
   * @param {HTMLElement} viewport
   * @param {HTMLElement[]} slides
   */
  #scrollIndex(viewport, slides) {
    if (slides.length === 0) return 0;
    const left = viewport.scrollLeft;
    let best = 0;
    let bestDist = Infinity;
    for (let i = 0; i < slides.length; i++) {
      const target = slides[i].offsetLeft;
      const dist = Math.abs(left - target);
      if (dist < bestDist) {
        bestDist = dist;
        best = i;
      }
    }
    return best;
  }

  /**
   * @param {HTMLElement[]} slides
   * @param {number} index
   * @param {ScrollBehavior} behavior
   */
  #scrollToIndex(slides, index, behavior) {
    slides[index]?.scrollIntoView({ behavior, inline: "start", block: "nearest" });
  }

  /**
   * @param {HTMLElement} viewport
   * @param {HTMLButtonElement | null} prevBtn
   * @param {HTMLButtonElement | null} nextBtn
   */
  #syncArrows(viewport, prevBtn, nextBtn) {
    const maxScroll = this.#maxScroll(viewport);
    const left = viewport.scrollLeft;
    prevBtn?.toggleAttribute("disabled", left <= SCROLL_EPS);
    nextBtn?.toggleAttribute("disabled", left >= maxScroll - SCROLL_EPS);
  }

  /**
   * @param {HTMLElement | null} progressBar
   * @param {HTMLElement} viewport
   * @param {number} slideCount
   */
  #syncProgress(progressBar, viewport, slideCount) {
    if (!progressBar || slideCount <= 1) return;
    const maxScroll = this.#maxScroll(viewport);
    const progress = maxScroll === 0 ? 0 : Math.max(0, Math.min(1, viewport.scrollLeft / maxScroll));
    const translateX = progress * (slideCount - 1) * 100;
    progressBar.style.transform = `translateX(${translateX}%)`;
  }

  connectedCallback() {
    const viewportNode = this.querySelector(".gallery__viewport");
    if (!viewportNode) return;

    this.disconnectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    const prevBtn = this.querySelector("[data-gallery-prev]");
    const nextBtn = this.querySelector("[data-gallery-next]");
    const progressBar = this.querySelector("[data-gallery-progress-bar]");

    const syncAll = () => {
      requestAnimationFrame(() => {
        const slides = this.#getSlides(viewportNode);
        const slideCount = slides.length;
        this.#syncArrows(viewportNode, prevBtn, nextBtn);
        this.#syncProgress(progressBar, viewportNode, slideCount);
      });
    };

    prevBtn?.addEventListener(
      "click",
      () => {
        const slides = this.#getSlides(viewportNode);
        const i = this.#scrollIndex(viewportNode, slides);
        if (i > 0) this.#scrollToIndex(slides, i - 1, "smooth");
      },
      { signal }
    );

    nextBtn?.addEventListener(
      "click",
      () => {
        const slides = this.#getSlides(viewportNode);
        const i = this.#scrollIndex(viewportNode, slides);
        if (i < slides.length - 1) this.#scrollToIndex(slides, i + 1, "smooth");
      },
      { signal }
    );

    viewportNode.addEventListener("scroll", syncAll, { signal, passive: true });
    viewportNode.addEventListener("scrollend", syncAll, { signal, passive: true });

    this.#resizeObserver = new ResizeObserver(() => syncAll());
    this.#resizeObserver.observe(viewportNode);

    const expandBtn = this.querySelector("[data-gallery-expand]");
    expandBtn?.addEventListener(
      "click",
      (event) => {
        const slides = this.#getSlides(viewportNode);
        const index = this.#scrollIndex(viewportNode, slides);
        this.closest("media-gallery")?.zoom(index, event);
      },
      { signal }
    );

    const slides = this.querySelectorAll(".gallery__slide");
    slides.forEach((slide, index) => {
      slide.addEventListener(
        "click",
        (event) => {
          if (event.target.closest("[data-gallery-expand], [data-gallery-prev], [data-gallery-next]")) return;
          this.closest("media-gallery")?.zoom(index, event);
        },
        { signal }
      );
    });

    const zoomDialog = this.closest("media-gallery")?.querySelector("product-gallery-zoom");
    zoomDialog?.addEventListener(
      "zoom-media:selected",
      (event) => {
        const slides = this.#getSlides(viewportNode);
        const index = event.detail?.index ?? 0;
        if (slides[index]) this.#scrollToIndex(slides, index, "smooth");
      },
      { signal }
    );

    syncAll();
  }

  disconnectedCallback() {
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = undefined;
    this.#abortController?.abort();
    this.#abortController = undefined;
  }
}

if (!customElements.get("product-gallery-carousel")) {
  customElements.define("product-gallery-carousel", ProductGalleryCarouselElement);
}
